import { useMemo } from 'react'
import { Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  useProjectCreateWizard,
  type ProjectCreateProjectTypeId,
} from '../ProjectCreateWizardContext'
import { ProjectCreateNavigation } from '../ProjectCreateNavigation'

function formatMonthYear(date: Date) {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = String(date.getFullYear())
  return `${month}-${year}`
}

function defaultProjectName(projectTypeLabel: string | null) {
  const label = projectTypeLabel || 'Projet'
  return `${label} ${formatMonthYear(new Date())}`
}

// Construction type → default VAT mapping (Belgian tax rules)
const CONSTRUCTION_TYPE_OPTIONS = [
  { value: 'new_construction', label: 'Construction neuve', vatPercent: 21 },
  { value: 'renovation', label: 'Rénovation', vatPercent: 6 },
  { value: 'co_contractor', label: 'Co-contractant', vatPercent: 0 },
]

export function Step3ProjectDetails({
  onCreate,
  onBack,
  currentStep,
  projectTypeOptions,
  clientSummary,
  jobSiteSummary,
  existingProjectsAtAddress,
}: {
  onCreate: () => void
  onBack: () => void
  currentStep: number
  projectTypeOptions: Array<{ id: ProjectCreateProjectTypeId; label: string; icon?: string | null }>
  clientSummary?: string | null
  jobSiteSummary?: string | null
  existingProjectsAtAddress?: Array<{ id: string; name: string; statusLabel?: string }>
}) {
  const { draft, setProjectField } = useProjectCreateWizard()
  const { isDirectSale } = draft.project

  const selectedTypeLabel = useMemo(() => {
    if (!draft.project.projectTypeId) return null
    return projectTypeOptions.find((o) => o.id === draft.project.projectTypeId)?.label || null
  }, [draft.project.projectTypeId, projectTypeOptions])

  const placeholder = useMemo(() => {
    if (isDirectSale) return `Vente directe ${formatMonthYear(new Date())}`
    return defaultProjectName(selectedTypeLabel)
  }, [selectedTypeLabel, isDirectSale])

  // For direct sales: project name must be explicitly entered (no auto-fill)
  const projectNameValid = isDirectSale
    ? draft.project.projectName.trim().length > 0
    : draft.project.projectNameTouched
      ? draft.project.projectName.trim().length > 0
      : true

  const canCreate = isDirectSale
    ? draft.project.vatPercent !== null &&
      projectNameValid &&
      (draft.isNewClient ? true : draft.isNewJobSite || !!draft.selectedJobSiteId)
    : !!draft.project.projectTypeId &&
      projectNameValid &&
      (draft.isNewClient ? true : draft.isNewJobSite || !!draft.selectedJobSiteId)

  // Derive selected construction type from vatPercent
  const selectedConstructionType = useMemo(() => {
    if (draft.project.vatPercent === null) return ''
    return CONSTRUCTION_TYPE_OPTIONS.find((o) => o.vatPercent === draft.project.vatPercent)?.value ?? ''
  }, [draft.project.vatPercent])

  const handleToggleDirectSale = (value: boolean) => {
    setProjectField('isDirectSale', value)
    if (value) {
      // Reset project name for direct sales — user must enter it
      setProjectField('projectName', '')
      setProjectField('projectNameTouched', true)
    } else {
      // Switching back to standard — restore auto-fill behavior
      setProjectField('projectNameTouched', false)
      setProjectField('projectName', '')
      setProjectField('vatPercent', null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Quel projet voulez-vous créer ?</h2>
        <p className="text-muted-foreground">Type et nom du projet</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {(clientSummary || jobSiteSummary) && (
          <div className="rounded-lg border bg-muted/30 p-4 space-y-1">
            {clientSummary && <p className="text-sm text-muted-foreground">Client : {clientSummary} ✓</p>}
            {jobSiteSummary && <p className="text-sm text-muted-foreground">Chantier : {jobSiteSummary} ✓</p>}
          </div>
        )}

        {existingProjectsAtAddress && existingProjectsAtAddress.length > 0 && (
          <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
            <p className="text-sm text-muted-foreground">⚠️ À cette adresse, vous avez déjà :</p>
            <div className="space-y-1">
              {existingProjectsAtAddress.map((p) => (
                <p key={p.id} className="text-sm text-muted-foreground">
                  • {p.name}{p.statusLabel ? ` (${p.statusLabel})` : ''}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Mode de commande toggle */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Mode de commande</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={!isDirectSale ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleToggleDirectSale(false)}
              data-testid="wizard-with-quote-toggle"
            >
              Avec devis
            </Button>
            <Button
              type="button"
              variant={isDirectSale ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleToggleDirectSale(true)}
              data-testid="wizard-direct-sale-toggle"
            >
              <Zap className="mr-1 h-3 w-3" />
              Vente directe
            </Button>
          </div>
          {isDirectSale && (
            <p className="text-xs text-muted-foreground">
              Pas de devis — vous composez la commande directement et facturez en fin de projet.
            </p>
          )}
        </div>

        {/* Project type (required for standard, optional for direct sale) */}
        {!isDirectSale && (
          <div className="space-y-2">
            <Label>Type de projet *</Label>
            <Select
              value={draft.project.projectTypeId || ''}
              onValueChange={(v) => {
                const typeId = v as ProjectCreateProjectTypeId
                setProjectField('projectTypeId', typeId)

                if (!draft.project.projectNameTouched) {
                  const label = projectTypeOptions.find((o) => o.id === typeId)?.label || 'Projet'
                  setProjectField('projectName', defaultProjectName(label))
                }
              }}
            >
              <SelectTrigger data-testid="project-type-select">
                <SelectValue placeholder="Sélectionner" />
              </SelectTrigger>
              <SelectContent>
                {projectTypeOptions.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.icon ? `${o.icon} ${o.label}` : o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Construction type for direct sale → sets default VAT */}
        {isDirectSale && (
          <div className="space-y-2">
            <Label>Type de construction *</Label>
            <Select
              value={selectedConstructionType}
              onValueChange={(v) => {
                const option = CONSTRUCTION_TYPE_OPTIONS.find((o) => o.value === v)
                if (option) {
                  setProjectField('vatPercent', option.vatPercent)
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner le type de construction" />
              </SelectTrigger>
              <SelectContent>
                {CONSTRUCTION_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label} — TVA {o.vatPercent}%
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Détermine le taux de TVA par défaut pour les lignes de commande.
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label>Nom du projet *</Label>
          <Input
            data-testid="project-name-input"
            placeholder={placeholder}
            value={isDirectSale ? draft.project.projectName : (draft.project.projectNameTouched ? draft.project.projectName : draft.project.projectName || placeholder)}
            onChange={(e) => {
              if (!draft.project.projectNameTouched) {
                setProjectField('projectNameTouched', true)
              }
              setProjectField('projectName', e.target.value)
            }}
          />
          {isDirectSale ? (
            <p className="text-xs text-muted-foreground">Donnez un nom à ce projet.</p>
          ) : (
            <p className="text-xs text-muted-foreground">Auto-généré selon le type et la date. Vous pouvez modifier.</p>
          )}
        </div>

        {!draft.isNewClient && (
          <div className="rounded-lg border bg-muted/30 p-4">
            <p className="text-sm text-muted-foreground">
              💡 Si vous voulez modifier un projet existant, annulez et ouvrez-le depuis le dashboard.
            </p>
          </div>
        )}

        <ProjectCreateNavigation
          currentStep={currentStep}
          onBack={onBack}
          onNext={onCreate}
          nextLabel="Créer le projet →"
          canGoNext={canCreate}
        />
      </div>
    </div>
  )
}
